import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  apiJsonClient,
  ApiJsonClientError,
} from "@/lib/query/core/api-json-client";
import { usePrivateMutation } from "@/lib/query/core/use-private-mutation";
import { keys } from "@/lib/i18n/portal";
import { endpoints } from "@/lib/api/endpoints";
import type {
  WhatsappMetaAnalytics,
  WhatsappMetaHistory,
  WhatsappAccount,
  WhatsappBilling,
  WhatsappBinding,
  WhatsappBindingInput,
  WhatsappCreateTemplate,
  WhatsappEditTemplate,
  WhatsappMessageDetail,
  WhatsappMessagePage,
  WhatsappOverview,
  WhatsappPurpose,
  WhatsappTemplateAcknowledgment,
  WhatsappTemplatePage,
  WhatsappUsagePage,
} from "../types/whatsapp.types";
const base = "/admin/whatsapp";
type Resources = {
  analytics: WhatsappMetaAnalytics;
  history: WhatsappMetaHistory;
  account: WhatsappAccount;
  overview: WhatsappOverview;
  billing: WhatsappBilling;
  messages: WhatsappMessagePage;
  usage: WhatsappUsagePage;
  templates: WhatsappTemplatePage;
};
export function useWhatsappQuery<K extends keyof Resources>(
  resource: K,
  query = "",
  enabled = true,
) {
  return useQuery({
    queryKey: ["admin", "whatsapp", resource, query],
    queryFn: ({ signal }) =>
      apiJsonClient<Resources[K]>(
        `${base}/${resource}${query ? `?${query}` : ""}`,
        { signal },
      ),
    enabled,
    retry: false,
  });
}
export function useWhatsappMessage(id: string) {
  return useQuery({
    queryKey: ["admin", "whatsapp", "message", id],
    queryFn: ({ signal }) =>
      apiJsonClient<WhatsappMessageDetail>(
        `${base}/messages/${encodeURIComponent(id)}`,
        { signal },
      ),
    retry: false,
  });
}
export function useWhatsappWrite() {
  const client = useQueryClient();
  return usePrivateMutation({
    retry: false,
    mutationFn: (input: {
      method: "POST" | "PATCH" | "DELETE" | "PUT";
      path: string;
      data?:
        | WhatsappCreateTemplate
        | WhatsappEditTemplate
        | WhatsappBindingInput;
    }) =>
      apiJsonClient<
        WhatsappTemplateAcknowledgment | { success: true } | WhatsappBinding
      >(`${base}/${input.path}`, {
        method: input.method,
        headers: { "Content-Type": "application/json" },
        body: input.data === undefined ? undefined : JSON.stringify(input.data),
      }),
    // A successful remote write stays successful if its follow-up read fails.
    onSuccess: () => {
      void client
        .invalidateQueries({ queryKey: ["admin", "whatsapp"] })
        .catch(() => undefined);
    },
  });
}
export function useWhatsappWabaWrite() {
  const client = useQueryClient();
  return usePrivateMutation({
    retry: false,
    mutationFn: (value: string) =>
      apiJsonClient(
        endpoints.admin.appConfigByKey("WHATSAPP_BUSINESS_ACCOUNT_ID"),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ value }),
        },
      ),
    onSuccess: () => {
      void client
        .invalidateQueries({ queryKey: ["admin"] })
        .catch(() => undefined);
    },
  });
}
const definiteWriteFailures = new Set([
  "WHATSAPP_NOT_CONFIGURED",
  "WHATSAPP_PROVIDER_PERMISSION_DENIED",
  "WHATSAPP_TEMPLATE_IN_USE",
  "WHATSAPP_MANAGEMENT_BUSY",
  "WHATSAPP_TEMPLATE_INCOMPATIBLE",
  "WHATSAPP_BINDING_INVALID",
  "WHATSAPP_PROVIDER_REJECTED",
  "WHATSAPP_TEMPLATE_INVALID",
  "WHATSAPP_TEMPLATE_NOT_FOUND",
  "WHATSAPP_MEMBERSHIP_UNVERIFIED",
]);
export function isUncertainWrite(error: unknown): boolean {
  if (!(error instanceof ApiJsonClientError)) return true;
  if (definiteWriteFailures.has(error.code ?? "")) return false;
  return (
    error.status >= 500 ||
    Boolean(
      error.code?.includes("UNKNOWN") || error.code?.includes("UNCERTAIN"),
    )
  );
}
export function bindingPath(purpose: WhatsappPurpose) {
  return `bindings/${purpose}`;
}

export function useBindingTemplates(enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ["admin", "whatsapp", "binding-templates"],
    enabled,
    retry: false,
    initialPageParam: "",
    queryFn: ({ pageParam, signal }) =>
      apiJsonClient<WhatsappTemplatePage>(
        `${base}/templates?limit=100&status=APPROVED${pageParam ? `&after=${encodeURIComponent(pageParam)}` : ""}`,
        { signal },
      ),
    getNextPageParam: (page) => page.after ?? undefined,
  });
}

export function whatsappFailureKey(error: unknown, write = false): string {
  const copy = keys.whatsapp;
  if (write && isUncertainWrite(error)) return copy.uncertainWrite;
  if (!(error instanceof ApiJsonClientError))
    return write ? copy.writeFailed : copy.loadFailed;
  const code = error.code;
  if (code === "WHATSAPP_DATE_RANGE_INVALID") return copy.dateError;
  if (code === "WHATSAPP_NOT_CONFIGURED") return copy.configRequired;
  if (code === "WHATSAPP_PROVIDER_PERMISSION_DENIED" || error.status === 403)
    return copy.permissionDenied;
  if (code === "WHATSAPP_TEMPLATE_IN_USE") return copy.inUseHint;
  if (code === "WHATSAPP_MANAGEMENT_BUSY") return copy.managementBusy;
  if (
    code === "WHATSAPP_TEMPLATE_INCOMPATIBLE" ||
    code === "WHATSAPP_BINDING_INVALID"
  )
    return copy.incompatible;
  if (
    code === "WHATSAPP_PROVIDER_REJECTED" ||
    code === "WHATSAPP_TEMPLATE_INVALID"
  )
    return copy.providerRejected;
  return write ? copy.writeFailed : copy.loadFailed;
}
